import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { engagementsApi } from '../../api/engagements.js';
import { speakersApi } from '../../api/speakers.js';
import { eventsApi } from '../../api/events.js';
import { userLookupApi } from '../../api/userLookup.js';
import { StatusBadge } from '../../components/common/StatusBadge.jsx';
import { LoadingSpinner } from '../../components/common/LoadingSpinner.jsx';
import { ErrorAlert } from '../../components/common/ErrorAlert.jsx';
import { ChecklistTab } from './tabs/ChecklistTab.jsx';
import { FilesTab } from './tabs/FilesTab.jsx';
import { CommsTab } from './tabs/CommsTab.jsx';
import { ReviewTab } from './tabs/ReviewTab.jsx';
import { InviteTab } from './tabs/InviteTab.jsx';
import { ENGAGEMENT_STATUSES } from '../../utils/constants.js';
import { formatDate, formatTimezone, dueState } from '../../utils/dates.js';
import { useQuickEmail } from '../../hooks/useQuickEmail.js';
import { usePageHeader } from '../../hooks/usePageHeader.jsx';

function speakerName(speaker) {
  if (!speaker) return null;
  return `${speaker.preferredName ?? speaker.firstName} ${speaker.lastName}`;
}

// "Portal" — not "Invite": by the time an engagement exists, the speaker has
// already agreed. This tab generates their passwordless portal link for
// submitting materials — actual speaker-recruitment invitations happen
// earlier, while the speaker is still in the pre-booking pipeline
// (Speakers → "Send email" → Initial outreach).
const TABS = ['Info', 'Checklist', 'Files', 'Comms', 'Review', 'Portal'];

export function EngagementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [engagement, setEngagement] = useState(null);
  const [speaker, setSpeaker] = useState(null);
  const [event, setEvent] = useState(null);
  const [ownerEmail, setOwnerEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('Info');
  const [saving, setSaving] = useState(false);
  const sendQuickEmail = useQuickEmail(engagement, event, speaker);
  usePageHeader(
    engagement?.talkTitle ?? speakerName(speaker) ?? 'Engagement',
    [speakerName(speaker), event?.name, event?.date && formatDate(event.date), event?.location].filter(Boolean).join(' · ') || undefined
  );

  const load = async () => {
    try {
      const eng = await engagementsApi.get(id);
      setEngagement(eng);
      const [spk, evt] = await Promise.all([
        speakersApi.get(eng.speakerId),
        eventsApi.get(eng.eventId),
      ]);
      setSpeaker(spk);
      setEvent(evt);
      if (eng.ownerId) {
        userLookupApi.lookup([eng.ownerId])
          .then((matches) => setOwnerEmail(matches?.[0]?.email ?? null))
          .catch(() => {}); // non-critical — falls back to showing the raw id
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const updateStatus = async (status) => {
    setSaving(true);
    try {
      const updated = await engagementsApi.update(id, { status });
      setEngagement(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteEngagement = async () => {
    if (!confirm('Remove this speaker from the event? This cannot be undone.')) return;
    await engagementsApi.remove(id);
    navigate(event ? `/events/${event.id}/edit` : speaker ? `/speakers/${speaker.id}` : '/events');
  };

  const pendingCount = engagement?.pendingSubmissions?.filter((s) => s.reviewStatus === 'pending').length ?? 0;

  if (loading) return <LoadingSpinner label="Loading engagement…" />;
  if (error && !engagement) return <ErrorAlert error={error} />;

  return (
    <div className="page-container">
      {/* Breadcrumb — this is a speaker's talk at a specific event, reached
          by drilling into either one, not a separate "Engagements" section. */}
      <nav aria-label="Breadcrumb" style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--space-4)', color: 'var(--color-text-muted)' }}>
        {event ? (
          <Link to={`/events/${event.id}/edit`} className="link">{event.name}</Link>
        ) : (
          <span>Event</span>
        )}
        <span style={{ margin: '0 var(--space-1)' }}>›</span>
        {speaker ? (
          <Link to={`/speakers/${speaker.id}`} className="link">{speakerName(speaker)}</Link>
        ) : (
          <span>{speakerName(speaker) ?? 'Loading…'}</span>
        )}
      </nav>

      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      {/* Status controls — title/subtitle now live in the page banner */}
      <div className="between" style={{ flexWrap: 'wrap', gap: 'var(--space-3)', marginBottom: 'var(--space-6)', justifyContent: 'flex-end' }}>
        <div className="inline">
          <StatusBadge status={engagement.status} />
          <select
            className="form-select"
            value={engagement.status}
            onChange={(e) => updateStatus(e.target.value)}
            disabled={saving}
            aria-label="Change status"
            style={{ fontSize: 'var(--text-sm)' }}
          >
            {ENGAGEMENT_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <Link to={`/engagements/${id}/edit`} className="btn btn-secondary btn-sm">Edit</Link>
          <button className="btn btn-danger btn-sm" onClick={deleteEngagement}>Delete</button>
        </div>
      </div>

      {/* Meta strip */}
      <div className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3) var(--space-4)' }}>
        <dl className="meta-grid">
          <div>
            <dt>Agreement</dt>
            <dd className="between" style={{ gap: 'var(--space-2)' }}>
              <span>{engagement.agreementStatus}</span>
              {engagement.agreementStatus !== 'Received' && speaker?.email && (
                <button className="btn btn-secondary btn-sm" onClick={() => sendQuickEmail('Missing agreement')}>
                  Email
                </button>
              )}
            </dd>
          </div>
          <div>
            <dt>Next due</dt>
            <dd className="between" style={{ gap: 'var(--space-2)' }}>
              <span style={{ color: ['overdue', 'today'].includes(dueState(engagement.nextDueDate)) ? 'var(--color-danger)' : undefined }}>
                {formatDate(engagement.nextDueDate)}
              </span>
              {['overdue', 'today'].includes(dueState(engagement.nextDueDate)) && speaker?.email && (
                <button className="btn btn-secondary btn-sm" onClick={() => sendQuickEmail('Due-date reminder')}>
                  Email
                </button>
              )}
            </dd>
          </div>
          <div>
            <dt>Last comm</dt>
            <dd>{formatDate(engagement.lastCommDate)}{engagement.commType && ` (${engagement.commType})`}</dd>
          </div>
          <div>
            <dt>Owner</dt>
            <dd>{ownerEmail ?? (engagement.ownerId ? 'Loading…' : '—')}</dd>
          </div>
        </dl>
      </div>

      {/* Tabs */}
      <div role="tablist" className="tab-list" aria-label="Engagement sections">
        {TABS.map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            className={`tab-btn${tab === t ? ' active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t}
            {t === 'Review' && pendingCount > 0 && (
              <span className="badge badge-warning" style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 'var(--radius-full)' }}>
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="tab-panel" role="tabpanel">
        {tab === 'Info' && (
          <InfoTab engagement={engagement} speaker={speaker} event={event} onUpdate={setEngagement} />
        )}
        {tab === 'Checklist' && (
          <ChecklistTab
            engagementId={id}
            checklist={engagement.checklist}
            onUpdate={setEngagement}
            speakerEmail={speaker?.email}
            sendQuickEmail={sendQuickEmail}
          />
        )}
        {tab === 'Files' && (
          <FilesTab engagement={engagement} onUpdate={setEngagement} />
        )}
        {tab === 'Comms' && (
          <CommsTab engagement={engagement} event={event} speaker={speaker} />
        )}
        {tab === 'Review' && (
          <ReviewTab engagement={engagement} onUpdate={setEngagement} />
        )}
        {tab === 'Portal' && (
          <InviteTab engagement={engagement} event={event} speaker={speaker} onUpdate={setEngagement} />
        )}
      </div>
    </div>
  );
}

function InfoTab({ engagement, speaker, event, onUpdate }) {
  const navigate = useNavigate();
  return (
    <div className="stack">
      {/* Speaker info card */}
      <div className="card">
        <div className="between" style={{ marginBottom: 'var(--space-3)' }}>
          <h3 style={{ fontSize: 'var(--text-base)' }}>Speaker</h3>
          {speaker && <Link to={`/speakers/${speaker.id}`} className="btn btn-secondary btn-sm">View profile</Link>}
        </div>
        {speaker ? (
          <dl className="meta-grid">
            <div><dt>Name</dt><dd>{speakerName(speaker)}</dd></div>
            <div><dt>Email</dt><dd><a href={`mailto:${speaker.email}`} className="link">{speaker.email}</a></dd></div>
            <div><dt>Organization</dt><dd>{speaker.organization ?? '—'}</dd></div>
            <div><dt>Phone</dt><dd>{speaker.phone ?? '—'}</dd></div>
          </dl>
        ) : <p style={{ color: 'var(--color-text-muted)' }}>Speaker not found</p>}
      </div>

      {/* Event info card */}
      <div className="card">
        <div className="between" style={{ marginBottom: 'var(--space-3)' }}>
          <h3 style={{ fontSize: 'var(--text-base)' }}>Event</h3>
          {event && <Link to={`/events/${event.id}/edit`} className="btn btn-secondary btn-sm">View event</Link>}
        </div>
        {event ? (
          <dl className="meta-grid">
            <div><dt>Name</dt><dd>{event.name}</dd></div>
            <div>
              <dt>Date</dt>
              <dd>
                {formatDate(event.date)}
                {event.startTime && ` · ${event.startTime}${event.endTime ? `–${event.endTime}` : ''}`}
                {event.timeZone && ` · ${formatTimezone(event.timeZone)}`}
              </dd>
            </div>
            <div><dt>Format</dt><dd>{event.format ?? '—'}</dd></div>
            <div><dt>Location</dt><dd>{event.location ?? '—'}</dd></div>
            {event.pduCode && <div><dt>PDU code</dt><dd>{event.pduCode}</dd></div>}
          </dl>
        ) : <p style={{ color: 'var(--color-text-muted)' }}>Event not found</p>}
      </div>

      {/* Talk abstract */}
      <div className="card">
        <h3 style={{ fontSize: 'var(--text-base)', marginBottom: 'var(--space-3)' }}>Talk abstract</h3>
        {engagement.talkAbstract ? (
          <p className="prose" style={{ fontSize: 'var(--text-sm)', whiteSpace: 'pre-wrap' }}>{engagement.talkAbstract}</p>
        ) : (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
            No abstract yet. Add one from the <Link to={`/engagements/${engagement.id}/edit`} className="link">Edit</Link> page.
          </p>
        )}
      </div>

      {/* Biography */}
      <div className="card">
        <h3 style={{ fontSize: 'var(--text-base)', marginBottom: 'var(--space-3)' }}>Biography</h3>
        {engagement.bio ? (
          <p className="prose" style={{ fontSize: 'var(--text-sm)', whiteSpace: 'pre-wrap' }}>{engagement.bio}</p>
        ) : (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>No biography on file. Speaker can submit via portal.</p>
        )}
      </div>

      {/* Coordination notes */}
      {engagement.coordinationNotes && (
        <div className="card">
          <h3 style={{ fontSize: 'var(--text-base)', marginBottom: 'var(--space-3)' }}>Coordination notes</h3>
          <p className="prose" style={{ fontSize: 'var(--text-sm)', whiteSpace: 'pre-wrap' }}>{engagement.coordinationNotes}</p>
        </div>
      )}

      {/* Timestamps */}
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-subtle)' }}>
        Created {formatDate(engagement.createdAt)}
        {engagement.completedAt && ` · Completed ${formatDate(engagement.completedAt)}`}
      </p>
    </div>
  );
}
